/**
 * Севооборот поля — TanStack Query поверх `/api/v1/fields/{id}/rotation`.
 *
 * Читает таблицу лет и пишет год ЦЕЛИКОМ (`PUT`). Год — единица правки, потому
 * что «одновременно / одна за другой» это свойство года, а не отдельной строки.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { fieldCardKeys } from '../api';

export interface RotationEntry {
  sowing_id: string;
  kind: 'crop' | 'fallow';
  crop_id: string | null;
  /** Строка номенклатуры хозяйства — ключ строки в выпадашке культур. */
  local_catalog_item_id: string | null;
  crop_name: string | null;
  fallow_kind: string | null;
  area_ha: number | null;
  share_pct: number | null;
  yield_c_ha: number | null;
  variety_names: string[];
  status: string | null;
  sowing_date: string | null;
  harvest_date: string | null;
  is_perennial: boolean;
  perennial_year: number | null;
  /** Год посева переходящего посева — правка года дописывает укосы В ТОТ ЖЕ посев. */
  perennial_start_year: number | null;
  uprooting_year: number | null;
  /** Укосы ЭТОГО года, ц/га. Без них правка года стирала бы разбивку. */
  cuts_c_ha: number[];
  termination_kind: string | null;
  termination_date: string | null;
  source: 'manual' | 'import';
  sequence_no: number;
}

export interface RotationYear {
  year: number;
  season_id: string | null;
  editable: boolean;
  is_current: boolean;
  mode: 'empty' | 'single' | 'parallel' | 'sequential';
  entries: RotationEntry[];
  occupied_ha: number | null;
  predecessor_label: string | null;
}

export interface RotationView {
  field_id: string;
  field_name: string | null;
  field_area_ha: number | null;
  current_year: number;
  years: RotationYear[];
  empty_years: number[];
}

export interface RotationEntryBody {
  kind: 'crop' | 'fallow';
  crop_id?: string | null;
  local_catalog_item_id?: string | null;
  fallow_kind?: string | null;
  siderat_crop_id?: string | null;
  area_ha?: number | null;
  share_pct?: number | null;
  yield_c_ha?: number | null;
  variety_ids?: string[];
  sowing_date?: string | null;
  harvest_date?: string | null;
  comment?: string | null;
  is_perennial?: boolean;
  perennial_start_year?: number | null;
  cuts_c_ha?: number[];
  termination_kind?: string | null;
  termination_date?: string | null;
}

export interface RotationYearBody {
  mode: 'single' | 'parallel' | 'sequential';
  entries: RotationEntryBody[];
  change_reason?: string | null;
}

export const rotationKeys = {
  view: (fieldId: string, depth: number) => ['field-rotation', fieldId, depth] as const,
};

export function useRotation(fieldId: string, depth = 10): UseQueryResult<RotationView, Error> {
  return useQuery<RotationView, Error>({
    queryKey: rotationKeys.view(fieldId, depth),
    enabled: Boolean(fieldId),
    queryFn: () => api.get<RotationView>(`/api/v1/fields/${fieldId}/rotation?depth=${depth}`),
    staleTime: 30_000,
  });
}

/**
 * Сохранение года. Инвалидируем и историю культур карточки — она читает те же
 * посевы, и после ввода истории обязана показать то же самое (иначе агроном
 * увидит два разных ответа на один вопрос на соседних вкладках).
 */
export function usePutRotationYear(fieldId: string) {
  const qc = useQueryClient();
  return useMutation<RotationYear, Error, { year: number; body: RotationYearBody }>({
    mutationFn: ({ year, body }) =>
      api.put<RotationYear>(`/api/v1/fields/${fieldId}/rotation/${year}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['field-rotation', fieldId] });
      void qc.invalidateQueries({ queryKey: fieldCardKeys.cropHistory(fieldId) });
    },
  });
}
